import React from 'react';
import {
  Activity, Wallet, Info, TrendingUp, LineChart, AlertTriangle, PieChart, Target, RefreshCw, ShieldCheck
} from 'lucide-react';
import { MacroState, MacroIndicators, Holding } from '../types';
import { getRegimeAllocation, MarketRegime } from '../constants/regimes';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip as ReTooltip,
  PieChart as RePieChart, Pie, Cell
} from 'recharts';
import { getMarketStatus, getMarketStatusColor, formatCurrency, formatBps } from '../utils/market';
import { calculatePortfolioAllocation, getSector } from '../utils/portfolio';
import { TooltipHelp } from './CommonUI';
import { MacroTrendPopover } from './MacroTrendPopover';
import { RegimeAllocationPie } from './RegimeAllocationPie';
import { MacroIndicatorAuditModal } from './MacroIndicatorAuditModal';

import { DASHBOARD_CONFIG } from '../constants/dashboard';
import { useAppConfig } from '../hooks/useAppConfig';

interface MacroRegimesCardProps {
  macro: MacroState;
  indicators?: MacroIndicators | null;
  holdings?: Holding[];
  language?: string;
  marketMode?: 'US' | 'KR';
  mode?: 'full' | 'strategic' | 'items';
  noCard?: boolean;
  isLoading?: boolean;
}

export const formatCompactUnit = (val: number | undefined | null, defaultUnit: string = ''): string => {
  if (val === undefined || val === null) return '-';
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1000000) {
    const num = abs / 1000000;
    return `${sign}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}M`;
  }
  if (abs >= 1000) {
    const num = abs / 1000;
    return `${sign}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)}K`;
  }
  return `${sign}${abs.toLocaleString()}${defaultUnit}`;
};

export function MacroRegimesCard({
  macro,
  indicators,
  holdings = [],
  language = 'ko',
  marketMode = 'US',
  mode = 'full',
  noCard = false,
  isLoading = false
}: MacroRegimesCardProps) {
  const lang = language || 'ko';

  const translateValue = (val: string) => {
    if (lang !== 'ko' || !val) return val;
    const mapping: Record<string, string> = {
      'Tightening': '긴축', 'Easing': '완화', 'Neutral': '중립', 'Flat': '보합',
      'Contracting': '수축', 'Expanding': '확장', 'Restrictive': '긴축적', 'Expansive': '부양적',
      'Blackout': '블랙아웃', 'Pre-FOMC': 'FOMC 전', 'Post-FOMC': 'FOMC 후', 'Normal': '정상',
      'Hawkish': '매파적', 'Dovish': '비둘기파적', 'Wait & See': '관망', 'Pivot': '피벗',
      'Recovery': '회복', 'Growth': '성장', 'Real M2': '실질 M2', 'M2 Growth': 'M2 증가율',
      'Exports': '수출 증가율', 'Goldilocks': '골디락스', 'Reflation': '리플레이션',
      'Stagflation': '스태그플레이션', 'Deflation': '디플레이션'
    };
    return mapping[val] || val;
  };

  const [activeMobileTab, setActiveMobileTab] = React.useState<'regime' | 'stats' | 'liquidity' | 'bond'>('stats');
  const [isAuditModalOpen, setIsAuditModalOpen] = React.useState(false);

  const renderChange = (change: number | undefined | null, isReverse = false, text = '') => {
    if (change === undefined || change === null) return null;
    const isPositive = change >= 0;
    const colorClass = isReverse
      ? (isPositive ? 'text-red-400' : 'text-emerald-400')
      : (isPositive ? 'text-emerald-400' : 'text-red-400');
    return (
      <div className={`text-[10px] font-bold ${colorClass} mt-0.5`}>
        {isPositive ? '+' : ''}{change.toFixed(2)}%
        {text && <span className="opacity-40 text-[8px] ml-1">{text}</span>}
      </div>
    );
  };

  const [activeTrend, setActiveTrend] = React.useState<{
    id: string; label: string; unit: string; color: string; rect: DOMRect | null;
    prefix: string; divisor: number; decimals: number; currentValue?: number; frequency?: string;
  } | null>(null);

  const handleTrendClick = (
    e: React.MouseEvent, id: string, label: string, unit: string, color: string,
    prefix: string = '', divisor: number = 1, decimals: number = 2,
    currentValue?: number, frequency?: string
  ) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTrend({ id, label, unit, color, rect, prefix, divisor, decimals, currentValue, frequency });
  };

  const getLiquidityColor = (val: string) => {
    if (val === 'Tightening') return 'text-red-400';
    if (val === 'Easing') return 'text-emerald-400';
    return 'text-blue-400';
  };

  const getCreditColor = (val: string) => {
    if (val === 'Contracting') return 'text-red-400';
    if (val === 'Expanding') return 'text-emerald-400';
    return 'text-blue-400';
  };

  const getPolicyColor = (val: string) => {
    if (val === 'Restrictive' || val === 'Hawkish') return 'text-red-400';
    if (val === 'Expansive' || val === 'Post-FOMC' || val === 'Dovish') return 'text-emerald-400';
    if (val === 'Blackout' || val === 'Pre-FOMC' || val === 'Wait & See') return 'text-amber-400';
    if (val === 'Pivot') return 'text-blue-400';
    return 'text-slate-400';
  };

  const portfolioAllocation = holdings.length > 0 ? calculatePortfolioAllocation(holdings) : null;
  const currentRegime = (macro.marketRegime || 'Neutral') as MarketRegime;
  
  const { regimes: dbRegimes } = useAppConfig();
  const dbRegimeData = dbRegimes.find(r => r.market === marketMode && r.regime === currentRegime);
  
  const normalize = (val: any): number => {
    const num = typeof val === 'string' ? parseFloat(val) : Number(val);
    if (isNaN(num)) return 0;
    return num > 1.0 ? num / 100.0 : num;
  };

  let suggestedAllocation = getRegimeAllocation(currentRegime, marketMode);
  if (dbRegimeData) {
    suggestedAllocation = {
      equities: normalize(dbRegimeData.equities_weight),
      bonds: normalize(dbRegimeData.bonds_weight),
      commodities: normalize(dbRegimeData.commodities_weight),
      cash: normalize(dbRegimeData.cash_weight),
      description: {
        ko: dbRegimeData.description_ko || suggestedAllocation.description.ko,
        en: dbRegimeData.description_en || suggestedAllocation.description.en,
      },
      sectors: dbRegimeData.sectors_json ? (typeof dbRegimeData.sectors_json === 'string' ? JSON.parse(dbRegimeData.sectors_json) : dbRegimeData.sectors_json) : suggestedAllocation.sectors,
    };
  }

  const regimes = [
    {
      label: lang === 'ko' ? '유동성' : 'Liquidity',
      value: translateValue(macro.regimes.liquidity),
      icon: Activity, color: getLiquidityColor(macro.regimes.liquidity),
      help: lang === 'ko' ? '시장 내 유동성 공급 상태 (Tightening: 긴축, Easing: 완화)' : 'Market liquidity supply state.'
    },
    {
      label: lang === 'ko' ? '신용' : 'Credit',
      value: translateValue(macro.regimes.credit),
      icon: Wallet, color: getCreditColor(macro.regimes.credit),
      help: lang === 'ko' ? '신용 순환 주기 및 대출 여건 (Contracting: 수축, Expanding: 확장)' : 'Credit cycle and lending conditions.'
    },
    {
      label: lang === 'ko' ? '정책' : 'Policy',
      value: translateValue(macro.regimes.policy),
      icon: Info, color: getPolicyColor(macro.regimes.policy),
      help: lang === 'ko' ? '주요 정책 일정 및 통화 정책 기조' : 'Major policy schedule and monetary stance.'
    },
  ];

  const showStrategic = mode === 'full' || mode === 'strategic';
  const showItems = mode === 'full' || mode === 'items';

  // Helper for indicator card widget
  const IndicatorCard = ({ id, label, unit, color, value, change, date, releaseDate, prefix = '', divisor = 1, decimals = 2, onClick }: {
    id: string; label: string; unit: string; color: string;
    value?: number; change?: number; date?: string; releaseDate?: string;
    prefix?: string; divisor?: number; decimals?: number;
    onClick?: (e: React.MouseEvent) => void;
  }) => (
    <div
      className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
      onClick={onClick}
    >
      <div className="text-[10px] text-white/40 mb-1">{label}</div>
      <div className={`text-lg font-bold text-${color}-400`}>
        {prefix}{value !== undefined ? (value / divisor).toFixed(decimals) : '-'}{unit && <span className="text-[9px] opacity-60 ml-0.5">{unit}</span>}
      </div>
      {change !== undefined && (
        <div className={`text-[10px] ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </div>
      )}
      <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
        <div className="text-[9px] text-white/30 font-medium tracking-tight">{date || '-'}</div>
        <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
          발표: {(releaseDate || date || '').length >= 10 ? (releaseDate || date).substring(5).replace('-', '/') : (releaseDate || date || '-')}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`w-full ${!noCard ? 'bg-white/5 border border-white/10 p-6 rounded-3xl' : ''} space-y-6 relative`}>
      {isLoading && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center rounded-3xl border border-white/5">
          <div className="flex flex-col items-center gap-3 bg-black/40 px-6 py-4 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-md">
            <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
            <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase">
              {lang === 'ko' ? '거시 지표 업데이트 중...' : 'Updating Macro...'}
            </span>
          </div>
        </div>
      )}
      {(mode === 'items' || mode === 'full') && (
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            {lang === 'ko' ? '매크로 레짐' : 'Macro Regimes'}
          </h3>
          <button
            type="button"
            onClick={() => setIsAuditModalOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] font-bold bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 transition-all cursor-pointer shadow-sm active:scale-95"
            title={lang === 'ko' ? '주요 지표 수치 무결성 검증 센터 열기' : 'Open Macro Indicator Integrity Audit Center'}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>{lang === 'ko' ? '지표 검증됨' : 'Verified'}</span>
          </button>
        </div>
      )}


      {/* Strategic Block */}
      {showStrategic && macro.marketRegime && (
        <div className={`space-y-6 ${mode === 'strategic' && !noCard ? 'bg-white/5 border border-white/10 p-6 rounded-3xl' : ''}`}>
          {mode === 'strategic' && (
            <h3 className="font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              {lang === 'ko' ? '전략적 매크로 국면 분석' : 'Strategic Macro Regime Analysis'}
            </h3>
          )}
          <div className="p-5 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-400" />
                <span className="text-lg font-bold text-white">{translateValue(macro.marketRegime)}</span>
              </div>
              <div className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 uppercase tracking-wider">Strategic</div>
            </div>
            <p className="text-sm text-white/70 mb-6 leading-relaxed bg-white/5 p-3 rounded-xl border border-white/5">
              {suggestedAllocation.description[lang as 'ko' | 'en'] || suggestedAllocation.description.en}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="h-[240px] w-full bg-white/5 rounded-2xl border border-white/5 p-4 flex flex-col items-center">
                <span className="text-[10px] text-white/40 uppercase font-bold tracking-widest mb-2">
                  {lang === 'ko' ? '권고 자산 비중' : 'Target Allocation'}
                </span>
                <RegimeAllocationPie allocation={suggestedAllocation} language={lang} />
              </div>
              <div className="h-[240px] w-full bg-white/5 rounded-2xl border border-white/5 p-4 flex flex-col items-center">
                <span className="text-[10px] text-white/40 uppercase font-bold tracking-widest mb-2">
                  {portfolioAllocation ? (lang === 'ko' ? '권고 vs 현재' : 'Target vs Current') : (lang === 'ko' ? '레짐 분석' : 'Regime Analysis')}
                </span>
                <div className="flex-1 w-full min-h-[180px] min-w-0 min-h-0 relative">
                  <ResponsiveContainer width="100%" height={180} minWidth={0} minHeight={0} debounce={50}>
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={[
                      { subject: lang === 'ko' ? '주식' : 'Equities', A: suggestedAllocation.equities * 100, B: portfolioAllocation ? portfolioAllocation.assets.equities * 100 : 0, fullMark: 100 },
                      { subject: lang === 'ko' ? '채권' : 'Bonds', A: suggestedAllocation.bonds * 100, B: portfolioAllocation ? portfolioAllocation.assets.bonds * 100 : 0, fullMark: 100 },
                      { subject: lang === 'ko' ? '원자재' : 'Commodities', A: suggestedAllocation.commodities * 100, B: portfolioAllocation ? portfolioAllocation.assets.commodities * 100 : 0, fullMark: 100 },
                      { subject: lang === 'ko' ? '현금' : 'Cash', A: suggestedAllocation.cash * 100, B: portfolioAllocation ? portfolioAllocation.assets.cash * 100 : 0, fullMark: 100 },
                    ]}>
                      <PolarGrid stroke="rgba(255,255,255,0.1)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                      <Radar name={lang === 'ko' ? '권고' : 'Benchmark'} dataKey="A" stroke="#10b981" strokeWidth={2} fill="#10b981" fillOpacity={0.15} />
                      {portfolioAllocation && <Radar name={lang === 'ko' ? '포트폴리오' : 'Portfolio'} dataKey="B" stroke="#60a5fa" strokeWidth={2} fill="#60a5fa" fillOpacity={0.25} />}
                      <ReTooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px' }} itemStyle={{ color: '#fff' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            {portfolioAllocation && (
              <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <Activity className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">{lang === 'ko' ? '리밸런싱 제안' : 'Rebalancing Suggestions'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items Block */}
      {showItems && (
        <div className={`space-y-6 ${mode === 'items' && !noCard ? 'bg-white/5 border border-white/10 p-6 rounded-3xl' : ''}`}>
          {mode === 'items' && (
            <h3 className="font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              <div className="flex items-center gap-3">
                <span>{lang === 'ko' ? '레짐 세부 항목' : 'Regime Details'}</span>
              </div>
            </h3>
          )}
          {/* Mobile Tabs */}
          <div className="md:hidden flex bg-black/40 border border-white/5 p-1 rounded-xl shadow-inner w-full mb-4 overflow-x-auto whitespace-nowrap scrollbar-none gap-1">
            {(['regime', 'stats', 'liquidity', 'bond'] as const).map(tab => (
              <button key={tab} type="button" onClick={() => setActiveMobileTab(tab)}
                className={`flex-1 py-2 px-3 rounded-lg text-[10px] font-black transition-all ${activeMobileTab === tab ? 'bg-emerald-500/25 text-emerald-400 border border-emerald-500/20' : 'text-white/40'}`}>
                {tab === 'regime' ? (lang === 'ko' ? '레짐' : 'Regime') : tab === 'stats' ? (lang === 'ko' ? '경제지표' : 'Economic') : tab === 'liquidity' ? (lang === 'ko' ? '유동성' : 'Liquidity') : (lang === 'ko' ? '채권/리스크' : 'Bond/Risk')}
              </button>
            ))}
          </div>

          {/* Regime Tab Content */}
          <div className={`space-y-4 ${activeMobileTab === 'regime' ? 'block' : 'hidden'} md:block`}>
            {regimes.map((regime) => (
              <div key={regime.label} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-white/5 ${regime.color}`}>
                    <regime.icon className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-white/60 flex items-center gap-1">
                      {regime.label} <TooltipHelp text={regime.help} />
                    </span>
                  </div>
                </div>
                <span className={`text-sm font-bold ${regime.color}`}>{regime.value}</span>
              </div>
            ))}
          </div>

          {/* Stats / Liquidity / Bond Tab Content (KR vs US) */}
          {marketMode === 'KR' ? (
            <KRSection
              indicators={indicators}
              lang={lang}
              activeMobileTab={activeMobileTab}
              handleTrendClick={handleTrendClick}
              renderChange={renderChange}
            />
          ) : (
            <USSection
              indicators={indicators}
              lang={lang}
              activeMobileTab={activeMobileTab}
              handleTrendClick={handleTrendClick}
              renderChange={renderChange}
            />
          )}
        </div>
      )}

      {activeTrend && (
        <MacroTrendPopover
          indicatorId={activeTrend.id} label={activeTrend.label} unit={activeTrend.unit}
          color={activeTrend.color} isOpen={!!activeTrend} onClose={() => setActiveTrend(null)}
          triggerRect={activeTrend.rect} frequency={activeTrend.frequency}
          prefix={activeTrend.prefix} divisor={activeTrend.divisor}
          currentValue={activeTrend.currentValue}
          yieldSpread10Y3M={indicators?.government?.yieldSpread10Y3M?.value}
        />
      )}

      <MacroIndicatorAuditModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        language={lang}
        marketMode={marketMode}
        indicators={indicators}
      />

      {/* Agentic Macro Analysis */}
      {macro?.ai_reasoning && (
        <div className="pt-4 border-t border-white/10 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <h4 className="text-sm font-bold text-emerald-400">
              {lang === 'ko' ? '매크로 국면별 자산 민감도 분석' : 'Macro Regime Asset Sensitivity Analysis'}
            </h4>
          </div>
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-sm text-white/80 leading-relaxed mb-4">
              {macro.ai_reasoning}
            </p>
            {(macro.benefited_tickers?.length > 0 || macro.harmed_tickers?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {macro.benefited_tickers && macro.benefited_tickers.length > 0 && (
                  <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                    <div className="text-[11px] font-bold text-emerald-400 mb-2">{lang === 'ko' ? '역사적 양(+)의 상관 자산군' : 'Historical Positive Correlation'}</div>
                    <div className="flex flex-wrap gap-2">
                      {macro.benefited_tickers.map((t: string) => (
                        <span key={t} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded text-xs font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {macro.harmed_tickers && macro.harmed_tickers.length > 0 && (
                  <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                    <div className="text-[11px] font-bold text-red-400 mb-2">{lang === 'ko' ? '역사적 음(-)의 상관 자산군' : 'Historical Negative Correlation'}</div>
                    <div className="flex flex-wrap gap-2">
                      {macro.harmed_tickers.map((t: string) => (
                        <span key={t} className="px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== KR Section =====
function KRSection({ indicators, lang, activeMobileTab, handleTrendClick, renderChange }: any) {
  const isLoading = !indicators;
  const ig = indicators?.government || {};

  const LoadingSkeleton = () => (
    <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5 animate-pulse">
      <div className="h-3 bg-white/10 rounded w-3/4 mx-auto mb-2" />
      <div className="h-6 bg-white/10 rounded w-1/2 mx-auto mb-1" />
      <div className="h-3 bg-white/5 rounded w-1/3 mx-auto mt-1" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats Tab: KR Economic Indicators (9종) */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'stats' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '주요 한국 경제 지표' : 'Key KR Indicators'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 9 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'usdkrw', label: 'USD/KRW', unit: '', color: 'amber', val: indicators.usdkrw?.value ?? 1350, chg: indicators.usdkrw?.change, dt: indicators.usdkrw?.date ?? '-', relDt: (indicators.usdkrw as any)?.releaseDate },
              { id: 'vkospi', label: 'VKOSPI', unit: '', color: 'red', val: indicators.vkospi?.value ?? 0, chg: indicators.vkospi?.change, dt: indicators.vkospi?.date ?? '-', relDt: (indicators.vkospi as any)?.releaseDate },
              { id: 'krCpi', label: lang === 'ko' ? '한국 CPI' : 'KR CPI', unit: '%', color: 'orange', val: ig.krCpi?.value ?? 0, chg: ig.krCpi?.change, dt: ig.krCpi?.date ?? '-', relDt: (ig.krCpi as any)?.releaseDate },
              { id: 'krExports', label: lang === 'ko' ? '수출 증가율' : 'Exports Growth', unit: '%', color: 'emerald', val: ig.krExports?.value ?? 0, dt: ig.krExports?.date ?? '-', relDt: (ig.krExports as any)?.releaseDate },
              { id: 'krBsi', label: 'BSI', unit: '', color: 'teal', val: ig.krBsi?.value, dt: ig.krBsi?.date ?? '-', relDt: (ig.krBsi as any)?.releaseDate },
              { id: 'krExportPrice', label: lang === 'ko' ? '수출물가' : 'Export Price', unit: '', color: 'cyan', val: ig.krExportPrice?.value ?? 0, dt: ig.krExportPrice?.date ?? '-', relDt: (ig.krExportPrice as any)?.releaseDate },
              { id: 'krImportPrice', label: lang === 'ko' ? '수입물가' : 'Import Price', unit: '', color: 'cyan', val: ig.krImportPrice?.value ?? 0, dt: ig.krImportPrice?.date ?? '-', relDt: (ig.krImportPrice as any)?.releaseDate },
              { id: 'krPpi', label: 'PPI', unit: '', color: 'fuchsia', val: ig.krPpi?.value ?? 0, dt: ig.krPpi?.date ?? '-', relDt: (ig.krPpi as any)?.releaseDate },
              { id: 'krFuturesNetBuy', label: lang === 'ko' ? '선물순매수' : 'Futures Net Buy', unit: 'K', color: 'blue', val: ig.krFuturesNetBuy?.value ?? 0, dt: ig.krFuturesNetBuy?.date ?? '-', relDt: (ig.krFuturesNetBuy as any)?.releaseDate },
            ].map((item: any) => (
              <div
                key={item.id}
                className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                onClick={(e) => handleTrendClick(
                  e, item.id, item.label, item.unit,
                  `#${item.color === 'blue' ? '60a5fa' : item.color === 'amber' ? 'f59e0b' : item.color === 'red' ? 'ef4444' : item.color === 'orange' ? 'fb923c' : item.color === 'emerald' ? '10b981' : item.color === 'violet' ? '8b5cf6' : item.color === 'teal' ? '14b8a6' : item.color === 'cyan' ? '06b6d4' : item.color === 'fuchsia' ? 'd946ef' : item.color === 'yellow' ? 'ca8a04' : '9a3412'}`,
                  '', 1, item.unit === 'K' || item.unit === '계약' ? 0 : 2, item.val
                )}
              >
                <div className="text-[10px] text-white/40 mb-1 truncate">{item.label}</div>
                <div className={`text-lg font-bold text-${item.color}-400 truncate`}>
                  {item.val !== undefined && item.val !== null ? (
                    (item.id === 'krFuturesNetBuy' || item.unit === 'K') ? formatCompactUnit(item.val) :
                    (item.unit === '조' || item.unit === 'bp') ? `${item.val.toFixed(1)}${item.unit}` :
                    `${item.val.toFixed(2)}${item.unit}`
                  ) : '-'}
                </div>
                {item.chg !== undefined && (
                  <div className={`text-[10px] ${item.chg >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {item.chg >= 0 ? '+' : ''}{item.chg.toFixed(2)}%
                  </div>
                )}
                <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
                  <div className="text-[9px] text-white/30 font-medium tracking-tight">{item.dt || '-'}</div>
                  <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
                    발표: {(item.relDt || item.dt || '').length >= 10 ? (item.relDt || item.dt).substring(5).replace('-', '/') : (item.relDt || item.dt || '-')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Liquidity Tab: KR Liquidity */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'liquidity' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '유동성 지표' : 'Liquidity Indicators'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'krCustomerDeposit', label: lang === 'ko' ? '예탁금' : 'Deposits', unit: '조', color: 'yellow', val: ig.krCustomerDeposit?.value, chg: ig.krCustomerDeposit?.change },
              { id: 'krMarginBalance', label: lang === 'ko' ? '신용융자' : 'Margin', unit: '조', color: 'amber', val: ig.krMarginBalance?.value, chg: ig.krMarginBalance?.change },
              { id: 'krM2', label: lang === 'ko' ? '한국 M2' : 'KR M2', unit: '%', color: 'violet', val: ig.krM2?.value, chg: ig.krM2?.change },
            ].map((item: any) => (
              <div
                key={item.id}
                className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                onClick={(e) => handleTrendClick(
                  e, item.id, item.label, item.unit,
                  `#${item.color === 'yellow' ? 'ca8a04' : item.color === 'amber' ? 'f59e0b' : item.color === 'violet' ? '8b5cf6' : item.color === 'emerald' ? '34d399' : 'f59e0b'}`,
                  '', 1, item.unit === 'K' || item.unit === '계약' ? 0 : 2, item.val
                )}
              >
                <div className="text-[10px] text-white/40 mb-1 truncate">{item.label}</div>
                <div className={`text-lg font-bold text-${item.color}-400 truncate`}>
                  {item.val !== undefined && item.val !== null ? (
                    (item.id === 'krFuturesNetBuy' || item.unit === 'K') ? formatCompactUnit(item.val) :
                    item.unit ? `${item.val.toFixed(1)}${item.unit}` :
                    typeof item.val === 'number' ? item.val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'
                  ) : '-'}
                </div>
                {item.chg !== undefined && renderChange(item.chg)}
                <div className="text-[9px] text-white/20 mt-1">-</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bond Tab: KR Bond/Risk */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'bond' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '채권 시장 및 리스크' : 'KR Bond & Risk'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 5 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'krCreditSpread', label: lang === 'ko' ? '신용 스프레드' : 'Credit Spread', unit: '%', color: 'red', val: ig.krCreditSpread?.value, dt: ig.krCreditSpread?.date ?? '-', relDt: (ig.krCreditSpread as any)?.releaseDate },
              { id: 'krYieldSpread', label: lang === 'ko' ? 'KR 금리차' : 'KR Yield Sprd', unit: '%', color: 'pink', val: ig.krYieldSpread?.value, dt: ig.krYieldSpread?.date ?? '-', relDt: (ig.krYieldSpread as any)?.releaseDate },
              { id: 'krCds', label: 'CDS', unit: 'bp', color: 'rose', val: ig.krCds?.value, chg: ig.krCds?.change, dt: ig.krCds?.date ?? '-', relDt: (ig.krCds as any)?.releaseDate },
              { id: 'krCpRate', label: lang === 'ko' ? 'CP 금리' : 'CP Rate', unit: '%', color: 'violet', val: ig.krCpRate?.value, dt: ig.krCpRate?.date ?? '-', relDt: (ig.krCpRate as any)?.releaseDate },
              { id: 'bokRate', label: lang === 'ko' ? '기준금리' : 'BOK Rate', unit: '%', color: 'blue', val: ig.bokRate?.value, chg: ig.bokRate?.change, dt: ig.bokRate?.date ?? '-', relDt: (ig.bokRate as any)?.releaseDate },
            ].map((item) => (
              <div
                key={item.id}
                className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                onClick={(e) => handleTrendClick(e, item.id, item.label, item.unit, `#${item.color === 'red' ? 'ef4444' : item.color === 'pink' ? 'ec4899' : item.color === 'rose' ? 'e11d48' : item.color === 'violet' ? 'a78bfa' : item.color === 'blue' ? '60a5fa' : 'ef4444'}`, '', 1, 2, item.val)}
              >
                <div className="text-[10px] text-white/40 mb-1">{item.label}</div>
                <div className={`text-lg font-bold text-${item.color}-400`}>
                  {item.val !== undefined && item.val !== null ? `${item.val.toFixed(2)}${item.unit}` : '-'}
                </div>
                {item.chg !== undefined && (
                  <div className={`text-[10px] ${item.chg >= 0 ? (item.id === 'bokRate' ? 'text-emerald-400' : 'text-red-400') : 'text-emerald-400'}`}>
                    {item.chg >= 0 ? '+' : ''}{item.chg.toFixed(2)}{item.unit}
                  </div>
                )}
                <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
                  <div className="text-[9px] text-white/30 font-medium tracking-tight">{item.dt || '-'}</div>
                  <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
                    발표: {(item.relDt || item.dt || '').length >= 10 ? (item.relDt || item.dt).substring(5).replace('-', '/') : (item.relDt || item.dt || '-')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== US Section =====
function USSection({ indicators, lang, activeMobileTab, handleTrendClick, renderChange }: any) {
  const isLoading = !indicators;
  const ig = indicators?.government || {};
  const ec = indicators?.economics || {};

  const LoadingSkeleton = () => (
    <div className="bg-white/5 rounded-xl p-3 text-center border border-white/5 animate-pulse">
      <div className="h-3 bg-white/10 rounded w-3/4 mx-auto mb-2" />
      <div className="h-6 bg-white/10 rounded w-1/2 mx-auto mb-1" />
      <div className="h-3 bg-white/5 rounded w-1/3 mx-auto mt-1" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats Tab: US Economic Indicators (12종) */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'stats' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '미국 주요 경제 지표' : 'Key US Economic Indicators'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 12 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'fedFunds', label: lang === 'ko' ? '기준 금리' : 'Fed Funds', unit: '%', color: 'blue', val: indicators.fedFunds?.value, chg: indicators.fedFunds?.change, dt: indicators.fedFunds?.date, relDt: (indicators.fedFunds as any)?.releaseDate },
              { id: 'inflation', label: lang === 'ko' ? '헤드라인 CPI' : 'Headline CPI', unit: '%', color: 'orange', val: indicators.inflation?.value, chg: indicators.inflation?.change, dt: indicators.inflation?.date, relDt: (indicators.inflation as any)?.releaseDate },
              { id: 'coreCpi', label: lang === 'ko' ? '근원 CPI' : 'Core CPI', unit: '%', color: 'red', val: ec.coreCpi?.value, chg: ec.coreCpi?.change, dt: ec.coreCpi?.date, relDt: (ec.coreCpi as any)?.releaseDate },
              { id: 'coreRetailSales', label: lang === 'ko' ? '근원소매판매' : 'Core Retail Sales', unit: '%', color: 'rose', val: ec.coreRetailSales?.value, chg: ec.coreRetailSales?.change, dt: ec.coreRetailSales?.date, relDt: (ec.coreRetailSales as any)?.releaseDate },
              { id: 'corePce', label: lang === 'ko' ? '근원 PCE' : 'Core PCE', unit: '%', color: 'pink', val: ec.corePce?.value, chg: ec.corePce?.change, dt: ec.corePce?.date, relDt: (ec.corePce as any)?.releaseDate },
              { id: 'ppi', label: 'PPI', unit: '%', color: 'amber', val: ec.ppi?.value, chg: ec.ppi?.change, dt: ec.ppi?.date, relDt: (ec.ppi as any)?.releaseDate },
              { id: 'unemployment', label: lang === 'ko' ? '실업률' : 'Unemployment', unit: '%', color: 'emerald', val: indicators.unemployment?.value, chg: indicators.unemployment?.change, dt: indicators.unemployment?.date, relDt: (indicators.unemployment as any)?.releaseDate },
              { id: 'payrolls', label: lang === 'ko' ? '비농업 고용' : 'Payrolls', unit: 'k', color: 'emerald', val: ec.payrolls?.value, chg: ec.payrolls?.change, dt: ec.payrolls?.date, relDt: (ec.payrolls as any)?.releaseDate },
              { id: 'initialClaims', label: lang === 'ko' ? '실업수당청구' : 'Initial Claims', unit: 'k', color: 'red', val: ec.initialClaims?.value, chg: ec.initialClaims?.change, dt: ec.initialClaims?.date, divisor: 1000, relDt: (ec.initialClaims as any)?.releaseDate },
              { id: 'retailSales', label: lang === 'ko' ? '소매판매' : 'Retail Sales', unit: '%', color: 'purple', val: ec.retailSales?.value, chg: ec.retailSales?.change, dt: ec.retailSales?.date, relDt: (ec.retailSales as any)?.releaseDate },
              { id: 'pmiManufacturing', label: lang === 'ko' ? '제조업 PMI' : 'ISM Mfg PMI', unit: '', color: 'teal', val: ec.pmiManufacturing?.value, chg: ec.pmiManufacturing?.change, dt: ec.pmiManufacturing?.date, relDt: (ec.pmiManufacturing as any)?.releaseDate },
              { id: 'pmiServices', label: lang === 'ko' ? '서비스업 PMI' : 'ISM Svc PMI', unit: '', color: 'cyan', val: ec.pmiServices?.value, chg: ec.pmiServices?.change, dt: ec.pmiServices?.date, relDt: (ec.pmiServices as any)?.releaseDate },
            ].map((item) => {
              const div = item.divisor || 1;
              const displayVal = item.val !== undefined && item.val !== null ? `${(item.val / div).toFixed(item.id === 'unemployment' ? 1 : item.id === 'payrolls' || item.id === 'initialClaims' ? 0 : 2)}${item.unit}` : '-';
              const displayChg = item.chg !== undefined && item.chg !== null ? `${item.chg >= 0 ? '+' : ''}${(item.chg / div).toFixed(item.id === 'payrolls' || item.id === 'initialClaims' ? 0 : 2)}${item.id === 'payrolls' || item.id === 'initialClaims' ? 'k' : '%'}` : null;
              return (
                <div
                  key={item.id}
                  className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                  onClick={(e) => handleTrendClick(e, item.id, item.label, item.unit, `#${item.color === 'blue' ? '60a5fa' : item.color === 'orange' ? 'fb923c' : item.color === 'red' ? 'f87171' : item.color === 'rose' ? 'fb7185' : item.color === 'pink' ? 'ec4899' : item.color === 'amber' ? 'f59e0b' : item.color === 'emerald' ? '34d399' : item.color === 'purple' ? 'a855f7' : item.color === 'teal' ? '14b8a6' : item.color === 'cyan' ? '06b6d4' : '60a5fa'}`, '', div, 2, item.val)}
                >
                  <div className="text-[10px] text-white/40 mb-1">{item.label}</div>
                  <div className={`text-lg font-bold text-${item.color}-400`}>
                    {displayVal}
                  </div>
                  {displayChg && (
                    <div className={`text-[10px] ${item.chg >= 0 ? (item.id === 'payrolls' || item.id === 'retailSales' || item.id === 'coreRetailSales' ? 'text-emerald-400' : item.id === 'unemployment' ? 'text-red-400' : 'text-red-400') : (item.id === 'payrolls' || item.id === 'retailSales' || item.id === 'coreRetailSales' ? 'text-red-400' : 'text-emerald-400')}`}>
                      {displayChg}
                    </div>
                  )}
                  <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
                  <div className="text-[9px] text-white/30 font-medium tracking-tight">{item.dt || '-'}</div>
                  <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
                    발표: {(item.relDt || item.dt || '').length >= 10 ? (item.relDt || item.dt).substring(5).replace('-', '/') : (item.relDt || item.dt || '-')}
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Liquidity Tab: US Liquidity */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'liquidity' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '유동성 지표' : 'Liquidity Indicators'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 7 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'fedBalanceSheet', label: lang === 'ko' ? '연준 총자산' : 'Fed Balance Sheet', unit: 'T', prefix: '$', color: 'blue', val: ig.fedBalanceSheet?.value, chg: ig.fedBalanceSheet?.change, dt: ig.fedBalanceSheet?.date, relDt: (ig.fedBalanceSheet as any)?.releaseDate },
              { id: 'tgaBalance', label: 'TGA 잔고', unit: 'T', prefix: '$', color: 'amber', val: ig.tgaBalance?.value, chg: ig.tgaBalance?.change, dt: ig.tgaBalance?.date, relDt: (ig.tgaBalance as any)?.releaseDate },
              { id: 'reverseRepo', label: lang === 'ko' ? '역레포' : 'Reverse Repo', unit: 'B', prefix: '$', color: 'violet', val: ig.reverseRepo?.value, chg: ig.reverseRepo?.change, dt: ig.reverseRepo?.date, relDt: (ig.reverseRepo as any)?.releaseDate },
              { id: 'fedReserves', label: lang === 'ko' ? '지급준비금' : 'Fed Reserves', unit: 'T', prefix: '$', color: 'indigo', val: ig.fedReserves?.value, chg: ig.fedReserves?.change, dt: ig.fedReserves?.date, relDt: (ig.fedReserves as any)?.releaseDate },
              { id: 'm2', label: 'M2 통화량', unit: 'T', prefix: '$', color: 'emerald', val: ig.m2?.value, chg: ig.m2?.change, dt: ig.m2?.date, relDt: (ig.m2 as any)?.releaseDate },
              { id: 'realM2', label: lang === 'ko' ? '실질 M2' : 'Real M2', unit: 'T', prefix: '$', color: 'cyan', val: ig.realM2?.value, chg: ig.realM2?.change, dt: ig.realM2?.date, relDt: (ig.realM2 as any)?.releaseDate },
              { id: 'velocityOfM2', label: lang === 'ko' ? 'M2 유통속도' : 'M2 Velocity', unit: '', prefix: '', color: 'purple', val: ig.velocityOfM2?.value, chg: ig.velocityOfM2?.change, dt: ig.velocityOfM2?.date, relDt: (ig.velocityOfM2 as any)?.releaseDate },
            ].map((item) => {
              let displayVal = '-';
              let divisor = 1;
              if (item.val !== undefined && item.val !== null) {
                if (item.unit === 'T') {
                  divisor = Math.abs(item.val) >= 100000 ? 1000000 : (Math.abs(item.val) >= 10 ? 1000 : 1);
                  displayVal = `${item.prefix}${(item.val / divisor).toFixed(2)}T`;
                } else if (item.unit === 'B') {
                  divisor = Math.abs(item.val) >= 100000 ? 1000 : 1;
                  displayVal = `${item.prefix}${(item.val / divisor).toFixed(2)}B`;
                } else {
                  divisor = 1;
                  displayVal = item.val.toFixed(2);
                }
              }
              return (
                <div
                  key={item.id}
                  className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                  onClick={(e) => handleTrendClick(e, item.id, item.label, item.unit, `#${item.color === 'amber' ? 'f59e0b' : item.color === 'blue' ? '3b82f6' : item.color === 'violet' ? '8b5cf6' : item.color === 'indigo' ? '6366f1' : item.color === 'emerald' ? '10b981' : item.color === 'cyan' ? '06b6d4' : 'a855f7'}`, item.prefix, divisor, 2, item.val)}
                >
                  <div className="text-[10px] text-white/40 mb-1">{item.label}</div>
                  <div className={`text-lg font-bold text-${item.color}-400`}>
                    {displayVal}
                  </div>
                  {item.chg !== undefined && (
                    <div className={`text-[10px] ${item.chg >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {item.chg >= 0 ? '+' : ''}{item.chg.toFixed(2)}%
                    </div>
                  )}
                  <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
                  <div className="text-[9px] text-white/30 font-medium tracking-tight">{item.dt || '-'}</div>
                  <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
                    발표: {(item.relDt || item.dt || '').length >= 10 ? (item.relDt || item.dt).substring(5).replace('-', '/') : (item.relDt || item.dt || '-')}
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bond Tab: US Bond/Risk */}
      <div className={`pt-4 border-t border-white/10 ${activeMobileTab === 'bond' ? 'block' : 'hidden'} md:block`}>
        <h4 className="text-sm font-bold text-white/80 mb-4">{lang === 'ko' ? '채권 시장 및 리스크' : 'Bond Market & Risk'}</h4>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => <LoadingSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { id: 'treasury3M', label: lang === 'ko' ? '3개월물 금리' : 'T-Bill 3M', unit: '%', color: 'amber', val: ig.treasury3M?.value, chg: ig.treasury3M?.change, dt: ig.treasury3M?.date, relDt: (ig.treasury3M as any)?.releaseDate },
              { id: 'treasury2Y', label: lang === 'ko' ? '2년물 국채 금리' : 'Treasury 2Y', unit: '%', color: 'blue', val: ig.treasury2Y?.value, chg: ig.treasury2Y?.change, dt: ig.treasury2Y?.date, relDt: (ig.treasury2Y as any)?.releaseDate },
              { id: 'treasury5Y', label: lang === 'ko' ? '5년물 국채 금리' : 'Treasury 5Y', unit: '%', color: 'indigo', val: ig.treasury5Y?.value, chg: ig.treasury5Y?.change, dt: ig.treasury5Y?.date, relDt: (ig.treasury5Y as any)?.releaseDate },
              { id: 'treasury10Y', label: lang === 'ko' ? '10년물 국채 금리' : 'Treasury 10Y', unit: '%', color: 'purple', val: ig.treasury10Y?.value, chg: ig.treasury10Y?.change, dt: ig.treasury10Y?.date, relDt: (ig.treasury10Y as any)?.releaseDate },
              { id: 'yieldSpread10Y3M', label: '10Y-3M 금리차', unit: 'bp', color: 'pink', val: ig.yieldSpread10Y3M?.value, chg: ig.yieldSpread10Y3M?.change, dt: ig.yieldSpread10Y3M?.date, relDt: (ig.yieldSpread10Y3M as any)?.releaseDate },
              { id: 'highYieldSpread', label: lang === 'ko' ? '하이일드 스프레드' : 'High Yield Spread', unit: 'bp', color: 'red', val: ig.highYieldSpread?.value, chg: ig.highYieldSpread?.change, dt: ig.highYieldSpread?.date, relDt: (ig.highYieldSpread as any)?.releaseDate },
            ].map((item) => (
              <div
                key={item.id}
                className="bg-white/5 rounded-xl p-3 text-center border border-white/5 relative group/period cursor-pointer hover:bg-white/10 transition-all"
                onClick={(e) => handleTrendClick(e, item.id, item.label, item.unit, `#${item.color === 'red' ? 'ef4444' : item.color === 'amber' ? 'f59e0b' : item.color === 'pink' ? 'ec4899' : item.color === 'blue' ? '3b82f6' : item.color === 'indigo' ? '6366f1' : 'a855f7'}`, '', 1, 2, item.val)}
              >
                <div className="text-[10px] text-white/40 mb-1">{item.label}</div>
                <div className={`text-lg font-bold text-${item.color}-400`}>
                  {item.val !== undefined && item.val !== null
                    ? (item.unit === 'bp' ? formatBps(item.val) : `${item.val.toFixed(2)}%`)
                    : '-'}
                </div>
                {item.chg !== undefined && item.chg !== null && (
                  <div className={`text-[10px] ${item.chg >= 0 ? (item.id === 'yieldSpread10Y3M' ? 'text-emerald-400' : 'text-red-400') : (item.id === 'yieldSpread10Y3M' ? 'text-red-400' : 'text-emerald-400')}`}>
                    {item.chg >= 0 ? '+' : ''}
                    {`${item.chg.toFixed(2)}%`}
                  </div>
                )}
                <div className="mt-1.5 pt-1 border-t border-white/5 flex flex-col items-center justify-center gap-0.5">
                  <div className="text-[9px] text-white/30 font-medium tracking-tight">{item.dt || '-'}</div>
                  <div className="text-[9px] text-purple-300 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded leading-none">
                    발표: {(item.relDt || item.dt || '').length >= 10 ? (item.relDt || item.dt).substring(5).replace('-', '/') : (item.relDt || item.dt || '-')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}